// Phase 44 — PRICE-02 · D-09 · D-10 · D-11 — <SuggestedPriceChip />.
//
// Pre-apply chip rendered on the canvas Pricing section's price field label
// row. Sibling (NOT subclass) of <ProvenanceChip>; on Apply, the parent
// conditionally re-renders and this component UNMOUNTS while the standard
// <ProvenanceChip source='AI' /> mounts in its place (UI-SPEC Hand-off #3).
//
// Three variants per D-10 / 44-UI-SPEC:
//   - Standard         (confidence ≥ 0.75)         blue-50 surface
//   - Low-confidence   (0 < confidence < 0.75)     amber-50 surface + <Info /> trailing
//   - CTA (zero-comps) (suggestedPrice === null || confidence === 0) amber-50 surface + 2px border + <AlertTriangle /> + [Run Comparables]
//
// Interaction contract:
//   - Click chip body → opens popover (D-09 — NOT hover)
//   - Click inline [Apply] → fires onApply + STOPS event propagation (T-44-05)
//   - CTA variant: body is decorative; only [Run Comparables] interactive
//
// Typography lock (UI-SPEC rev 2):
//   13px font-normal chip body + buttons. NO text-xs, NO font-medium.

import { Sparkles, AlertTriangle, Info } from 'lucide-react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@marine/ui/popover';
import { Button } from '@marine/ui/button';
import { cn } from '@/lib/utils';
import type { MarketBandComp } from '@marine/contracts/types/ai.types';
import { SuggestedPricePopover } from './SuggestedPricePopover';

type Variant = 'standard' | 'low-confidence' | 'cta';

export interface SuggestedPriceChipProps {
  suggestedPrice: number | null;
  reasoning: string;
  confidence: number;
  sourceComps: MarketBandComp[];
  onApply: () => void;
  onDismiss: () => void;
  onRunComparables: () => void;
}

function selectVariant(suggestedPrice: number | null, confidence: number): Variant {
  if (suggestedPrice === null || confidence === 0) return 'cta';
  if (confidence < 0.75) return 'low-confidence';
  return 'standard';
}

function formatCurrency(n: number): string {
  return n.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

/**
 * Phase 44 — PRICE-02 · D-09 · D-10 · D-11.
 *
 * Pre-apply sibling of `ProvenanceChip`. Post-apply, the parent swaps to a
 * standard `ProvenanceChip` with `source: 'AI'` via genuine unmount→remount
 * (NOT a variant prop on this component — UI-SPEC Hand-off #3).
 *
 * Click chip body → popover (D-09, NOT hover).
 * Click inline Apply → onApply (popover does NOT open; event.stopPropagation
 * guards against T-44-05 — price-field tampering via popover bubble).
 */
export function SuggestedPriceChip({
  suggestedPrice,
  reasoning,
  confidence,
  sourceComps,
  onApply,
  onDismiss,
  onRunComparables,
}: SuggestedPriceChipProps) {
  const variant = selectVariant(suggestedPrice, confidence);
  const confidenceInt = Math.round(confidence * 100);

  // Body text per variant (UI-SPEC §Copywriting Contract — verbatim).
  // Separator `·` is U+00B7 middle-dot; surrounded by single spaces.
  const bodyText =
    variant === 'cta'
      ? 'Not enough comps'
      : variant === 'low-confidence'
        ? `Low confidence: $${formatCurrency(suggestedPrice as number)} · ${confidenceInt}%`
        : `Suggested: $${formatCurrency(suggestedPrice as number)} · ${confidenceInt}%`;

  const ariaLabel =
    variant === 'cta'
      ? 'Not enough comparables available. Run Comparables to gather pricing data.'
      : `Suggested price: $${formatCurrency(suggestedPrice as number)}, ${confidenceInt}% confidence. Click for details.`;

  // Surface + border + text tokens per variant (Phase 43 inheritance).
  const surfaceClasses =
    variant === 'standard'
      ? 'bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 text-blue-700 dark:text-blue-300'
      : variant === 'low-confidence'
        ? 'bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200'
        : 'bg-amber-50 dark:bg-amber-950/30 border-2 border-amber-400 dark:border-amber-600 text-amber-800 dark:text-amber-200';

  // CTA variant — body is decorative; only the [Run Comparables] button is
  // interactive. The chip body carries the testid expected by Plan 08 e2e
  // (suggested-price-chip-body) so the page object can locate the chip in any
  // variant.
  if (variant === 'cta') {
    return (
      <div
        role="group"
        aria-label={ariaLabel}
        data-testid="suggested-price-chip-body"
        className={cn(
          'inline-flex items-center gap-2 px-2 py-1 rounded',
          'text-[0.8125rem] font-normal',
          surfaceClasses,
        )}
      >
        <AlertTriangle
          className="h-4 w-4 text-amber-600 dark:text-amber-400"
          role="img"
          aria-hidden="true"
        />
        <span className="text-[0.8125rem] font-normal">{bodyText}</span>
        <Button
          size="sm"
          variant="primary"
          onClick={onRunComparables}
          aria-label="Run Comparables agent to gather pricing data"
          data-testid="suggested-price-chip-run-comparables"
          className="text-[0.8125rem] font-normal"
        >
          Run Comparables
        </Button>
      </div>
    );
  }

  // Standard + Low-confidence variants: chip body is a <button> that opens
  // the popover on click. Inline [Apply] is a separate Tab-stop sibling whose
  // click handler stopPropagation's to guard against opening the popover
  // (T-44-05 — inline Apply must NOT trigger the popover bubble).
  const Icon = variant === 'standard' ? Sparkles : Sparkles;
  const iconColor =
    variant === 'standard'
      ? 'text-blue-600 dark:text-blue-400'
      : 'text-amber-600 dark:text-amber-400';

  return (
    <Popover>
      <div
        className={cn(
          'inline-flex items-center gap-2 px-2 py-1 rounded',
          surfaceClasses,
        )}
      >
        <Icon
          className={cn('h-4 w-4', iconColor)}
          role="img"
          aria-hidden="true"
        />
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={ariaLabel}
            data-testid="suggested-price-chip-body"
            className={cn(
              'text-[0.8125rem] font-normal bg-transparent border-0 p-0',
              'cursor-pointer outline-hidden',
              'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              'text-inherit',
              'inline-flex items-center gap-1',
            )}
          >
            <span>{bodyText}</span>
            {variant === 'low-confidence' && (
              <Info
                className="h-3 w-3 text-amber-600 dark:text-amber-400"
                role="img"
                aria-hidden="true"
              />
            )}
          </button>
        </PopoverTrigger>
        <Button
          size="sm"
          variant="primary"
          onClick={(e) => {
            // T-44-05 — prevent click from bubbling to the chip body (which
            // would open the popover). Inline Apply is a separate entry point
            // to onApply — same handler, no popover side effect.
            e.stopPropagation();
            onApply();
          }}
          aria-label={`Apply suggested price $${formatCurrency(suggestedPrice as number)}`}
          data-testid="suggested-price-chip-apply"
          className="text-[0.8125rem] font-normal"
        >
          Apply
        </Button>
      </div>
      <PopoverContent
        side="bottom"
        align="end"
        className="max-w-[400px] p-4 w-auto"
        data-testid="suggested-price-popover"
      >
        <SuggestedPricePopover
          suggestedPrice={suggestedPrice}
          reasoning={reasoning}
          sourceComps={sourceComps}
          onApply={onApply}
          onDismiss={onDismiss}
        />
      </PopoverContent>
    </Popover>
  );
}
